import { Box, Divider, Flex, Text } from '@chakra-ui/react'
import { Field } from '@hb/shared'

import { useMemo } from 'react'
import { ActionButton } from '../Buttons'
import { Condition } from '../forms'
import { FormElement } from '../forms/Input'
import { useGynCareView } from './context'
import { MedicationPreview, SupplementPreview } from './MedicationPreview'

const ConditionMedications = ({
  conditionPath,
  conditionName,
}: {
  conditionPath: string
  conditionName: string
}) => {
  const { getConditionMedications, handleDeleteMedication, openMedicationConditionModal } =
    useGynCareView().conditionMedicationModal
  const medications = useMemo(
    () => getConditionMedications(conditionPath),
    [conditionPath, getConditionMedications],
  )
  return (
    <Box pb={2}>
      <Text fontSize="lg">
        Medications for {conditionName} ({medications.length})
      </Text>
      {medications.map(medication => (
        <Box pb={2} key={medication.id}>
          <MedicationPreview
            onDelete={() => handleDeleteMedication('medication', medication.id, conditionPath)}
            onEditClick={() =>
              openMedicationConditionModal('medication', conditionPath, medication.id)
            }
            medication={medication}
          />
        </Box>
      ))}
      <ActionButton
        size="sm"
        onClick={() => openMedicationConditionModal('medication', conditionPath, `${Date.now()}`)}>
        + Add Medication
      </ActionButton>
    </Box>
  )
}

const ConditionSupplements = ({
  conditionPath,
  conditionName,
}: {
  conditionPath: string
  conditionName: string
}) => {
  const { getConditionSupplements, handleDeleteMedication, openMedicationConditionModal } =
    useGynCareView().conditionMedicationModal
  const supplements = useMemo(
    () => getConditionSupplements(conditionPath),
    [conditionPath, getConditionSupplements],
  )
  return (
    <Box pb={2}>
      <Text fontSize="lg" pb={1}>
        Supplements for {conditionName} ({supplements.length})
      </Text>
      {supplements.map(supplement => (
        <Box pb={2} key={supplement.id}>
          <SupplementPreview
            onDelete={() => handleDeleteMedication('supplement', supplement.id, conditionPath)}
            onEditClick={() =>
              openMedicationConditionModal('supplement', conditionPath, supplement.id)
            }
            supplement={supplement}
          />
        </Box>
      ))}
      <ActionButton
        size="sm"
        onClick={() => openMedicationConditionModal('supplement', conditionPath, `${Date.now()}`)}>
        + Add Supplement
      </ActionButton>
    </Box>
  )
}

export const ConditionGroupView = ({
  fieldGroup,
  stageId,
  basePath,
  noMedications = false,
}: {
  fieldGroup: Record<string, Field>
  stageId: string
  basePath: string
  noMedications?: boolean
}) => {
  const { conditionPath, conditionId, conditionName } = useMemo(() => {
    const [path, field] = Object.entries(fieldGroup)[0]
    return {
      conditionPath: `${stageId}.${basePath}.${path}`,
      conditionName: field.placeholder,
      conditionId: path,
    }
  }, [fieldGroup, stageId, basePath])

  return (
    <Flex flexFlow="column" w="100%">
      {Object.entries(fieldGroup).map(([key, field]) => (
        <FormElement
          key={key}
          field={field}
          active
          basePath={basePath ? `${basePath}.` : ''}
          name={key}
        />
      ))}
      {noMedications ? null : (
        <Condition basePath={basePath ? `${basePath}.` : ''} condition={v => !!v?.[conditionId]}>
          <ConditionMedications conditionPath={conditionPath} conditionName={conditionName} />
          <ConditionSupplements conditionPath={conditionPath} conditionName={conditionName} />
          <Divider my={2} />
        </Condition>
      )}
    </Flex>
  )
}
